/**
 * Crunch Design System — Input & InputGroup
 *
 * Figma components:
 *   input       (key: e0eb1ede9639d5de8d76660c5d7e06a13a05c9a9)
 *   input-group (key: 906f07ca02d2257449eb118fd382e0e8b9a904f5)
 *
 * Usage:
 *   <InputGroup label="Company name" description="Legal entity name" error="Required">
 *     <Input placeholder="Enter name" leadingIcon={<FontAwesomeIcon icon={faBuilding} />} />
 *   </InputGroup>
 */
import * as React from 'react';

/**
 * Attributes that tell password managers (1Password, LastPass, Bitwarden) and browser autofill to leave a field alone.
 * Applied by default to type="search"; use on any other non-credential or API-token field. Never on real login fields.
 */
export const PASSWORD_MANAGER_OPT_OUT = {
    autoComplete: "off",
    "data-1p-ignore": true,
    "data-lpignore": "true",
    "data-bwignore": true,
    "data-form-type": "other",
} as const;

export function Input({
    size = "default",
    error = false,
    readOnly = false,
    disabled = false,
    allowAutofill = false,
    leadingIcon,
    trailingIcon,
    className,
    ref,
    ...rest
}: {
    size?: "default" | "small";
    error?: boolean;
    readOnly?: boolean;
    disabled?: boolean;
    // Search inputs opt out of password managers by default; set to keep autofill on.
    allowAutofill?: boolean;
    leadingIcon?: React.ReactNode;
    trailingIcon?: React.ReactNode;
    className?: string;
    ref?: React.Ref<HTMLInputElement>;
    [key: string]: any;
}) {
    const cls = [
        "cr-input",
        size !== "default" && `cr-input--${size}`,
        error && "cr-input--danger",
        readOnly && "cr-input--readonly",
        disabled && "cr-input--disabled",
        className,
    ]
        .filter(Boolean)
        .join(" ");

    return (
        <div className={cls}>
            {leadingIcon && <span className="cr-input__icon">{leadingIcon}</span>}
            <input
                className="cr-input__control"
                readOnly={readOnly}
                disabled={disabled}
                ref={ref}
                {...(rest.type === "search" && !allowAutofill ? PASSWORD_MANAGER_OPT_OUT : {})}
                {...rest}
            />
            {trailingIcon && <span className="cr-input__icon">{trailingIcon}</span>}
        </div>
    );
}

export function InputGroup({
    layout = "stacked",
    label,
    description,
    error,
    annotation,
    tooltip,
    htmlFor,
    children,
    className,
    ...rest
}: {
    layout?: "stacked" | "horizontal";
    label?: string;
    description?: string;
    error?: string;
    annotation?: string;
    tooltip?: React.ReactNode;
    htmlFor?: string;
    children?: React.ReactNode;
    className?: string;
    [key: string]: any;
}) {
    const cls = [
        "cr-input-group",
        `cr-input-group--${layout}`,
        className,
    ]
        .filter(Boolean)
        .join(" ");

    return (
        <div className={cls} {...rest}>
            {(label || description) && (
                <div className="cr-input-group__text">
                    {label && (
                        <label className="cr-input-group__label" htmlFor={htmlFor}>
                            {label}
                            {tooltip && <span className="cr-input-group__tooltip">{tooltip}</span>}
                        </label>
                    )}
                    {description && (
                        <span className="cr-input-group__description">{description}</span>
                    )}
                </div>
            )}
            <div className="cr-input-group__field">
                {children}
                {error && <span className="cr-input-group__error">{error}</span>}
                {annotation && <span className="cr-input-group__annotation">{annotation}</span>}
            </div>
        </div>
    );
}
